import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, BellOff, KeyRound, Share, Plus, Smartphone } from 'lucide-react';

import { api, authApi, MIN_PASSWORD_LENGTH } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { cn } from '../lib/cn';
import { EmptyState, ErrorNote, Panel, Spinner } from '../components/ui';
import { Field, inputClass, Modal } from '../components/Modal';
import { isIos } from '../lib/platform';
import {
  getStatus,
  sendTest,
  subscribe,
  unsubscribe,
  type PushStatus,
} from '../lib/push';

interface EventDefinition {
  type: string;
  label: string;
  description?: string;
  group: string;
  defaultOn: boolean;
}

interface PreferencesResponse {
  preferences: Record<string, boolean>;
  events: EventDefinition[];
}

interface Subscription {
  subscriptionId: string;
  deviceLabel: string | null;
  createdAt: string;
  expired: boolean;
}

export function Settings(): React.JSX.Element {
  const { activeStore } = useAuth();
  const storeId = activeStore?.storeId;
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<PushStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);

  async function refreshStatus(): Promise<void> {
    try {
      setStatus(await getStatus());
    } catch {
      setStatus({ state: 'unavailable', reason: 'Could not check notification support.' });
    }
  }

  useEffect(() => {
    void refreshStatus();
  }, []);

  const { data, error, isPending } = useQuery({
    queryKey: ['notification-preferences', storeId],
    queryFn: () =>
      api.get<PreferencesResponse>(`/stores/${storeId}/notification-preferences`),
    enabled: Boolean(storeId),
  });

  const { data: devices } = useQuery({
    queryKey: ['push-subscriptions'],
    queryFn: () => api.get<{ subscriptions: Subscription[] }>('/push/subscriptions'),
  });

  const savePreference = useMutation({
    mutationFn: (values: { eventType: string; enabled: boolean }) =>
      api.put(`/stores/${storeId}/notification-preferences`, values),
    onMutate: async (values) => {
      // Optimistic: a toggle that waits for a round trip before moving feels
      // broken, and this is the kind of switch people flick several of at once.
      await queryClient.cancelQueries({ queryKey: ['notification-preferences', storeId] });
      const previous = queryClient.getQueryData<PreferencesResponse>([
        'notification-preferences',
        storeId,
      ]);

      if (previous) {
        queryClient.setQueryData<PreferencesResponse>(['notification-preferences', storeId], {
          ...previous,
          preferences: { ...previous.preferences, [values.eventType]: values.enabled },
        });
      }

      return { previous };
    },
    onError: (_error, _values, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['notification-preferences', storeId], context.previous);
      }
      setMessage({ tone: 'bad', text: 'That preference did not save. Try again.' });
    },
  });

  async function toggleSubscription(): Promise<void> {
    setBusy(true);
    setMessage(null);
    try {
      if (status?.state === 'subscribed') {
        await unsubscribe();
        setMessage({ tone: 'ok', text: 'This device will no longer receive notifications.' });
      } else {
        await subscribe();
        setMessage({ tone: 'ok', text: 'This device is now set up for notifications.' });
      }
      await refreshStatus();
    } catch (caught) {
      setMessage({
        tone: 'bad',
        text: caught instanceof Error ? caught.message : 'Could not change notifications.',
      });
    } finally {
      setBusy(false);
    }
  }

  async function test(): Promise<void> {
    setBusy(true);
    setMessage(null);
    try {
      const result = await sendTest();
      setMessage(
        result.sent > 0
          ? { tone: 'ok', text: `Test sent to ${result.sent} device${result.sent === 1 ? '' : 's'}.` }
          : { tone: 'bad', text: 'Nothing was delivered. Check notifications are allowed.' },
      );
    } catch (caught) {
      setMessage({
        tone: 'bad',
        text: caught instanceof Error ? caught.message : 'Could not send a test.',
      });
    } finally {
      setBusy(false);
    }
  }

  const groups = groupEvents(data?.events ?? []);

  return (
    <>
      <header className="mb-4">
        <p className="qs-eyebrow">Settings</p>
        <h1 className="text-lg font-medium tracking-tight">Account and notifications</h1>
      </header>

      <AccountPanel />

      {/* ------------------------------------------------------ this device */}
      <Panel title="This device" className="mb-3">
        <div className="p-3">
          {status === null ? (
            <Spinner label="Checking…" />
          ) : status.state === 'needs-install' ? (
            <IosInstallGuide reason={status.reason} />
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <span
                  className={cn(
                    'flex items-center gap-1.5 text-xs',
                    status.state === 'subscribed' ? 'text-up' : 'text-ink-muted',
                  )}
                >
                  {status.state === 'subscribed' ? (
                    <Bell size={14} aria-hidden />
                  ) : (
                    <BellOff size={14} aria-hidden />
                  )}
                  {status.state === 'subscribed'
                    ? 'Notifications are on for this device'
                    : 'Notifications are off for this device'}
                </span>

                {(status.state === 'prompt' || status.state === 'subscribed') && (
                  <div className="ml-auto flex gap-2">
                    {status.state === 'subscribed' && (
                      <button
                        type="button"
                        onClick={() => void test()}
                        disabled={busy}
                        className="rounded border border-rule px-2.5 py-1 text-xs text-ink-muted hover:border-accent hover:text-accent disabled:opacity-60"
                      >
                        Send a test
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => void toggleSubscription()}
                      disabled={busy}
                      className={cn(
                        'rounded px-2.5 py-1 text-xs font-medium disabled:opacity-60',
                        status.state === 'subscribed'
                          ? 'border border-rule text-ink-muted hover:border-down hover:text-down'
                          : 'bg-accent text-ink-inverse hover:bg-accent-hover',
                      )}
                    >
                      {busy
                        ? 'Working…'
                        : status.state === 'subscribed'
                          ? 'Turn off'
                          : 'Turn on'}
                    </button>
                  </div>
                )}
              </div>

              {status.reason && (
                <p className="mt-2 text-2xs text-ink-muted">{status.reason}</p>
              )}
            </>
          )}

          {message && (
            <p
              role="status"
              className={cn(
                'mt-2 text-2xs',
                message.tone === 'ok' ? 'text-up' : 'text-down',
              )}
            >
              {message.text}
            </p>
          )}
        </div>

        {(devices?.subscriptions.length ?? 0) > 0 && (
          <div className="border-t border-rule">
            <table className="w-full">
              <caption className="sr-only">Devices set up for notifications</caption>
              <tbody>
                {devices?.subscriptions.map((device) => (
                  <tr key={device.subscriptionId} className="border-b border-rule last:border-0">
                    <td className="py-1.5 pl-3 text-xs">
                      <Smartphone
                        size={12}
                        className="mr-1.5 inline text-ink-faint"
                        aria-hidden
                      />
                      {device.deviceLabel ?? 'Unknown device'}
                    </td>
                    <td className="py-1.5 pr-3 text-right text-2xs text-ink-faint">
                      {device.expired ? 'No longer reachable' : 'Active'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* ------------------------------------------------------ preferences */}
      <header className="mb-2">
        <h2 className="text-xs font-medium">What to send</h2>
        <p className="text-2xs text-ink-faint">
          These apply to {activeStore?.displayName ?? 'this store'} only, and to every device you
          have signed in on.
        </p>
      </header>

      {error && <ErrorNote error={error} />}

      {isPending ? (
        <Panel>
          <Spinner label="Loading preferences…" />
        </Panel>
      ) : groups.length === 0 ? (
        <Panel>
          <EmptyState>No notifications are available.</EmptyState>
        </Panel>
      ) : (
        <div className="grid gap-3">
          {groups.map(([group, events]) => (
            <Panel key={group} title={group}>
              <ul>
                {events.map((event) => {
                  const enabled = data?.preferences[event.type] ?? event.defaultOn;
                  return (
                    <li
                      key={event.type}
                      className="flex items-start gap-3 border-b border-rule p-3 last:border-0"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium">{event.label}</p>
                        {event.description && (
                          <p className="mt-0.5 text-2xs text-ink-muted">{event.description}</p>
                        )}
                      </div>

                      <button
                        type="button"
                        role="switch"
                        aria-checked={enabled}
                        aria-label={event.label}
                        onClick={() =>
                          savePreference.mutate({ eventType: event.type, enabled: !enabled })
                        }
                        /* The tap area is grown by a transparent pseudo-element
                           rather than by the track itself: a 36×20 switch is
                           half the 44px a thumb needs, and padding it out would
                           have made the switch look like a button. */
                        className={cn(
                          'relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors',
                          "before:absolute before:-inset-x-1.5 before:-inset-y-3 before:content-['']",
                          enabled ? 'bg-accent' : 'bg-rule-strong',
                        )}
                      >
                        {/* left-0 is load-bearing: without it an absolutely
                            positioned knob falls back to its static position
                            at the track's right edge, and the translate then
                            pushes it outside the track entirely — a white
                            circle on a white page, invisible. */}
                        <span
                          className={cn(
                            'absolute top-0.5 left-0 h-4 w-4 rounded-full bg-surface transition-transform',
                            enabled ? 'translate-x-4.5' : 'translate-x-0.5',
                          )}
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Panel>
          ))}
        </div>
      )}
    </>
  );
}

/** Who is signed in, and the way to change their password. */
function AccountPanel(): React.JSX.Element {
  const { user } = useAuth();
  const [changing, setChanging] = useState(false);
  const [changed, setChanged] = useState(false);

  return (
    <Panel title="Your account" className="mb-3">
      <div className="flex flex-wrap items-center justify-between gap-3 p-3">
        <div className="min-w-0">
          <p className="text-xs wrap-anywhere">{user?.fullName}</p>
          <p className="text-2xs wrap-anywhere text-ink-faint">{user?.email}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            setChanged(false);
            setChanging(true);
          }}
          className="flex items-center gap-1.5 rounded border border-rule px-2.5 py-1.5 text-xs text-ink-muted hover:text-ink"
        >
          <KeyRound size={14} aria-hidden />
          Change password
        </button>
      </div>

      {changed && (
        <p role="status" className="border-t border-rule px-3 py-2 text-2xs text-up">
          Password changed. Any other devices signed in to your account have been signed out.
        </p>
      )}

      {/* Mounted only while open, so a closed dialog leaves no typed password
          or stale error behind for the next time. */}
      {changing && (
        <ChangePasswordDialog
          email={user?.email ?? ''}
          onClose={() => setChanging(false)}
          onChanged={() => {
            setChanging(false);
            setChanged(true);
          }}
        />
      )}
    </Panel>
  );
}

function ChangePasswordDialog({
  email,
  onClose,
  onChanged,
}: {
  email: string;
  onClose: () => void;
  onChanged: () => void;
}): React.JSX.Element {
  const [error, setError] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: ({ current, next }: { current: string; next: string }) =>
      authApi.changePassword(current, next),
    onSuccess: onChanged,
    onError: (caught: unknown) =>
      setError(caught instanceof Error ? caught.message : 'Could not change the password.'),
  });

  return (
    <Modal open title="Change your password" onClose={onClose}>
      <form
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const current = String(form.get('currentPassword'));
          const next = String(form.get('newPassword'));

          if (next.length < MIN_PASSWORD_LENGTH) {
            setError(`Use at least ${MIN_PASSWORD_LENGTH} characters for the new password.`);
            return;
          }
          if (next !== String(form.get('confirmPassword'))) {
            setError('The two new passwords do not match.');
            return;
          }

          setError(null);
          change.mutate({ current, next });
        }}
      >
        {/* For password managers, which file a new password under the
            username in the same form. */}
        <input
          type="email"
          name="username"
          autoComplete="username"
          value={email}
          readOnly
          tabIndex={-1}
          aria-hidden
          className="sr-only"
        />

        <Field label="Current password" htmlFor="currentPassword">
          <input
            id="currentPassword"
            name="currentPassword"
            type="password"
            autoComplete="current-password"
            className={inputClass}
            autoFocus
            required
          />
        </Field>
        <Field
          label="New password"
          htmlFor="newPassword"
          hint={`At least ${MIN_PASSWORD_LENGTH} characters. Other devices will be signed out.`}
        >
          <input
            id="newPassword"
            name="newPassword"
            type="password"
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            className={inputClass}
            required
          />
        </Field>
        <Field label="Type the new password again" htmlFor="confirmPassword">
          <input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            autoComplete="new-password"
            className={inputClass}
            required
          />
        </Field>

        {error && (
          <p role="alert" className="mb-3 text-xs text-down">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-rule px-3 py-1.5 text-xs text-ink-muted hover:text-ink"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={change.isPending}
            className="rounded bg-accent px-3 py-1.5 text-xs font-medium text-ink-inverse hover:bg-accent-hover disabled:opacity-60"
          >
            {change.isPending ? 'Changing…' : 'Change password'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * The iOS install flow.
 *
 * Not an upsell. On iPhone and iPad, Safari delivers Web Push only to a PWA on
 * the home screen — without this the feature does nothing at all, with no error
 * and no explanation. The steps are spelled out because the Share menu is not
 * where anybody would think to look for "install".
 */
function IosInstallGuide({ reason }: { reason: string }): React.JSX.Element {
  return (
    <div>
      <p className="text-xs">{reason}</p>

      <ol className="mt-3 grid gap-2">
        <li className="flex items-center gap-2 text-2xs text-ink-muted">
          <span className="qs-figure rounded border border-rule px-1.5 py-0.5">1</span>
          Tap
          <Share size={13} className="text-accent" aria-hidden />
          <span className="font-medium text-ink">Share</span>
          in the Safari toolbar
        </li>
        <li className="flex items-center gap-2 text-2xs text-ink-muted">
          <span className="qs-figure rounded border border-rule px-1.5 py-0.5">2</span>
          Scroll down and tap
          <Plus size={13} className="text-accent" aria-hidden />
          <span className="font-medium text-ink">Add to Home Screen</span>
        </li>
        <li className="flex items-center gap-2 text-2xs text-ink-muted">
          <span className="qs-figure rounded border border-rule px-1.5 py-0.5">3</span>
          Open QuickStock from the home screen, then come back here
        </li>
      </ol>

      {!isIos() && (
        <p className="mt-3 text-2xs text-ink-faint">
          You are not on an iPhone or iPad, so this shouldn’t be showing. Reload the page.
        </p>
      )}
    </div>
  );
}

/** Keeps the plan's grouping, in the order the API declared them. */
function groupEvents(events: EventDefinition[]): [string, EventDefinition[]][] {
  const groups = new Map<string, EventDefinition[]>();
  for (const event of events) {
    const list = groups.get(event.group) ?? [];
    list.push(event);
    groups.set(event.group, list);
  }
  return [...groups.entries()];
}
